import { SearchOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import ITVSearch from 'components/ITVSearch';
import ITVTable from 'components/ITVTable';
import { selectConfiguration } from 'features/configuration/configurationSlice';
import { selectDepartment } from 'features/department/departmentSlice';
import EmployeeColumn from 'features/employee/components/EmployeeColumn';
import SearchPopup from 'features/employee/components/SearchPopup';
import { setCheckRow, setCheckAllStatus } from 'features/employee/employeeSlice';
import { useEmployeeListForContractV2 } from 'hooks/fetchers/useEmployeeListForContractV2';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { removeAccents } from 'utils/text';

export const ViewAddMember = (props: any) => {
  const { dataOrigin, setSelectUser } = props;
  const dispatch = useAppDispatch();
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const { t } = useTranslation(['insurance', 'employee', 'validation', 'overtime', 'timesheet']);
  const [sizePage, setSizePage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataOri, setDataOri] = useState<any[]>([]);
  const departments = useAppSelector(selectDepartment).data;
  const positions = useAppSelector(selectConfiguration).dataPosition as any;
  const [search, setSearch] = useState('');
  const [isFilter, setIsFilter] = useState(false);
  const filters = useMemo(() => ({ offset: 1, limit: 1000 }), []);
  const { data: employeeListApproved, isLoading: loadingListApproved } =
    useEmployeeListForContractV2(filters);

  // CRITICAL FIX: Local state để quản lý selected rows thay vì dùng Redux global state
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // CRITICAL FIX: Reset selection state khi component mount
  useEffect(() => {
    // Reset Redux state để tránh auto-select all từ state cũ
    dispatch(setCheckAllStatus(false));
    dispatch(setCheckRow([]));
    setSelectUser([]);
    setSelectedRowKeys([]);
  }, [dispatch, setSelectUser]);

  useEffect(() => {
    if (dataOrigin && employeeListApproved) {
      const copyArr = employeeListApproved?.filter(
        (item: any) => !dataOrigin?.some((value: any) => value.id === item?.id),
      );

      setData(copyArr);
      setDataOri(copyArr);
      setFilteredData(copyArr);
    }
    return () => {
      dispatch(setCheckRow([]));
      dispatch(setCheckAllStatus(false));
    };
  }, [employeeListApproved, dataOrigin, dispatch]);

  // Handle row selection change - update both local state and parent
  const handleSelectedRowKeysChange = (newSelectedRowKeys: string[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectUser(newSelectedRowKeys);
  };

  let COLUMNS = [
    {
      title: 'ID',
      dataIndex: 'employeeId',
      key: 'employeeId',
      ellipsis: true,
      width: 60,
      minWidth: 60,
      align: 'center',
    },
    {
      title: t('timesheet:Employee'),
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      minWidth: 200,
      ellipsis: true,
      render: function (text: any, record: any, index: any) {
        return (
          <EmployeeColumn
            avatar={
              record?.fullName ? `${record?.fullName}` : `${record?.lastName} ${record?.firstName}`
            }
            fullName={
              record?.fullName ? `${record?.fullName}` : `${record?.lastName} ${record?.firstName}`
            }
            email={record?.email}
          />
        );
      },
    },
    {
      title: t('employee:employee_information_fields.department_team'),
      dataIndex: 'departmentName',
      key: 'departmentName',
      width: 120,
      minWidth: 120,
      sorter: {
        compare: (a: any, b: any) => {
          if (a.departmentName && b.departmentName)
            return a.departmentName.localeCompare(b.departmentName);
          return 0;
        },
        multiple: 3,
      },
      ...getColumnSearchProps(
        'departmentName',
        departments?.map((item: any) => item.name),
      ),
    },
    {
      title: t('insurance:position'),
      dataIndex: 'positionName',
      key: 'positionName',
      ellipsis: true,
      width: 120,
      minWidth: 120,
      sorter: {
        compare: (a: any, b: any) => {
          if (a.positionName && b.positionName) return a.positionName.localeCompare(b.positionName);
          return 0;
        },
        multiple: 3,
      },
      ...getColumnSearchProps(
        'positionName',
        positions?.map((item: any) => item.name),
      ),
    },
  ];

  const onSearchList = (key: string) => {
    const text = removeAccents(key.toLowerCase());
    if (text) {
      const temp = dataOri.filter((item: any) => {
        return (
          removeAccents(item?.fullName?.toLowerCase())?.includes(text) ||
          removeAccents(item?.firstName?.toLowerCase())?.includes(text) ||
          removeAccents(item?.lastName?.toLowerCase())?.includes(text) ||
          removeAccents(item?.employeeId?.toLowerCase())?.includes(text)
        );
      });
      setSearch(text);
      setData(temp);
    } else {
      setSearch('');
      setData(dataOri);
    }
    setCurrentPage(1);
  };

  // handle onFilter clicked
  const onFilterPopupConfirm = (selectedKeys: any, confirm: any) => {
    // confirm && confirm();
  };

  // filter columns
  function getColumnSearchProps(dataIndex: any, options: any) {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <SearchPopup
          searchText={selectedKeys[0]}
          onPressEnter={() => confirm()}
          options={options}
          confirm={confirm}
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          onSearchTextChange={(e: any) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
          }}
          onClickFilter={(checkedValues: any) => onFilterPopupConfirm(selectedKeys, confirm)}
        />
      ),
      filterIcon: (filtered: any) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        return record?.[dataIndex] === value;
      },
    };
  }

  const handleChange = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: { currentDataSource: any },
  ) => {
    if (!filters?.departmentName?.length && !filters?.positionName?.length) {
      setIsFilter(false);
    } else {
      setIsFilter(true);
    }
    setFilteredData(extra.currentDataSource || []);
  };

  useEffect(() => {
    if (search) {
      const temp = dataOri.filter((item: any) => {
        return (
          removeAccents(item?.fullName?.toLowerCase())?.includes(search) ||
          removeAccents(item?.firstName?.toLowerCase())?.includes(search) ||
          removeAccents(item?.lastName?.toLowerCase())?.includes(search) ||
          removeAccents(item?.employeeId?.toLowerCase())?.includes(search)
        );
      });
      setSearch(search);
      setData(temp);
    } else {
      setSearch('');
      setData(dataOri);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isFilter]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ width: '200px', marginBottom: '20px' }}>
          <ITVSearch
            handleSearch={(value: any) => {
              onSearchList(value);
            }}
          />
        </div>
      </div>
      <ITVTable //can't invert last page when use itvtable, if change -> conflict to employee manage
        loading={loadingListApproved}
        columns={COLUMNS}
        height={550}
        data={data}
        isRowSelect={true}
        selectedRowKeys={selectedRowKeys}
        onSelectedRowKeysChange={handleSelectedRowKeysChange}
        setOffset={setCurrentPage}
        offset={currentPage}
        setLimit={setSizePage}
        limit={sizePage}
        rowKey={['id']}
        onChange={handleChange}
        filteredData={filteredData}
        totalResult={filteredData.length}
        totalIni={filteredData.length}
      />
    </div>
  );
};
